import { z } from "zod";

/**
 * The browser's public configuration, and the one place that decides which Supabase project a
 * running build talks to.
 *
 * The rule here is deliberately structural rather than procedural. A checkout with no environment
 * files at all — which is what a clean clone is — must run against the local Docker stack, and a
 * build that says it is local must be incapable of reaching a hosted project. Both properties used
 * to depend on a gitignored file being present and correct, which is the opposite of a guarantee.
 */

/** The Supabase CLI's fixed local values. Identical on every machine, and authenticate nothing. */
const LOCAL_SUPABASE_URL = "http://127.0.0.1:54321";
const LOCAL_PUBLISHABLE_KEY = "sb_publishable_ACJWlzQHlZjBrEguHvfOxg_3BJgxAaH";

const LOOPBACK_HOSTS = new Set(["127.0.0.1", "localhost", "[::1]", "0.0.0.0"]);

export const clientEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url().optional(),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(1).optional(),
  VITE_APP_ENV: z.enum(["local", "staging", "production"]).default("local"),
  // Public by design: a Turnstile sitekey is rendered into the page. The secret never leaves
  // Cloudflare and is never read here.
  VITE_TURNSTILE_SITEKEY: z.string().optional(),
  /**
   * The VAPID public key, which is public by construction: the browser has to hand it to the push
   * service to bind a subscription to us. Optional, because a deployment without it simply cannot
   * offer push, and `app/pwa/push.ts` says so on the screen rather than failing at the prompt.
   */
  VITE_VAPID_PUBLIC_KEY: z.string().optional()
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;

export function isLoopbackUrl(value: string): boolean {
  try {
    return LOOPBACK_HOSTS.has(new URL(value).hostname);
  } catch {
    return false;
  }
}

/**
 * Applies the local defaults and then refuses the one combination that has actually bitten this
 * repository: a local build pointed at a hosted Supabase project.
 *
 * Reaching staging now requires saying `VITE_APP_ENV=staging` out loud. An absent, empty or
 * partially filled environment falls back to the Docker stack instead of inheriting whatever a
 * developer happened to leave in a dotfile.
 */
export function resolveClientEnv(source: unknown): ClientEnv {
  const parsed = clientEnvSchema.parse(source);

  const resolved: ClientEnv =
    parsed.VITE_APP_ENV === "local"
      ? {
          ...parsed,
          VITE_SUPABASE_URL: parsed.VITE_SUPABASE_URL ?? LOCAL_SUPABASE_URL,
          VITE_SUPABASE_PUBLISHABLE_KEY:
            parsed.VITE_SUPABASE_PUBLISHABLE_KEY ?? LOCAL_PUBLISHABLE_KEY
        }
      : parsed;

  if (
    resolved.VITE_APP_ENV === "local" &&
    resolved.VITE_SUPABASE_URL &&
    !isLoopbackUrl(resolved.VITE_SUPABASE_URL)
  ) {
    throw new Error(
      "VITE_APP_ENV=local may only point at a loopback Supabase URL. " +
        "Set VITE_APP_ENV=staging explicitly to reach a hosted project."
    );
  }

  return resolved;
}

export const clientEnv = resolveClientEnv(import.meta.env);
